/* ============================================================
   9MM WOW ENGINE — full GSAP/ScrollTrigger choreography
   Loaded after gsap + ScrollTrigger + 9mm-3d.js + 9mm-energy-fx.js.
   Design rules:
   - Entrances hide elements ONLY via GSAP inline styles, tracked in a
     registry with a failsafe that force-reveals anything stuck hidden.
   - Elements we choreograph get their CSS reveal system neutralized
     (is-visible + transition:none) so CSS transitions never fight GSAP.
   - Scrub/parallax effects use transforms only (can never hide content).
   - Desktop gets the full show (pin, scrub, tilt); mobile a lighter pass.
   ============================================================ */
(function () {
  'use strict';

  if (!window.gsap || !window.ScrollTrigger) return;
  var gsap = window.gsap;
  gsap.registerPlugin(window.ScrollTrigger);
  var ST = window.ScrollTrigger;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce) return; // site is fully usable without the show

  /* ---------- helpers ---------- */

  // Neutralize the theme's CSS reveal system on elements GSAP will drive.
  function prep(els) {
    (els.forEach ? els : [els]).forEach(function (el) {
      el.classList.add('is-visible');
      el.classList.remove('nm-reveal', 'nm-reveal-scale');
      el.style.transition = 'none';
      el.style.filter = 'none';
    });
  }

  // Registry of elements currently hidden by an entrance; the failsafe
  // force-reveals anything that sits visible-in-viewport but never played.
  var pending = new Set();
  function hide(el, vars) {
    prep([el]);
    gsap.set(el, Object.assign({ autoAlpha: 0 }, vars || {}));
    pending.add(el);
  }
  function reveal(el, vars) {
    pending.delete(el);
    return gsap.to(el, Object.assign({ autoAlpha: 1, x: 0, y: 0, scale: 1, rotation: 0, rotationX: 0, rotationY: 0, skewY: 0, overwrite: 'auto' }, vars || {}));
  }
  function failsafe() {
    pending.forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) {
        reveal(el, { duration: 0.6, ease: 'power2.out' });
      }
    });
  }
  setInterval(failsafe, 2000);

  // Entrance batcher: hide now, play on enter with stagger.
  function entrance(selector, hiddenVars, playVars, staggerEach, scope) {
    var els = gsap.utils.toArray(selector, scope);
    if (!els.length) return;
    els.forEach(function (el) { hide(el, hiddenVars); });
    ST.batch(els, {
      start: 'top 90%',
      once: true,
      onEnter: function (batch) {
        batch.forEach(function (el, i) {
          reveal(el, Object.assign({
            duration: 0.9,
            ease: 'power3.out',
            delay: i * (staggerEach || 0.08)
          }, playVars || {}));
        });
      }
    });
  }

  // Count-up for a leading number in an element's first text node.
  function countUp(el, duration) {
    var node = el.firstChild;
    if (!node || node.nodeType !== 3) return;
    var m = node.textContent.match(/^\s*([0-9]+(?:[.,][0-9]+)?)/);
    if (!m) return;
    var raw = m[1];
    var decimals = (raw.split(/[.,]/)[1] || '').length;
    var comma = raw.indexOf(',') !== -1;
    var target = parseFloat(raw.replace(',', '.'));
    var suffix = node.textContent.slice(m[0].length);
    var obj = { v: 0 };
    var last = '';
    ST.create({
      trigger: el, start: 'top 92%', once: true,
      onEnter: function () {
        gsap.to(obj, {
          v: target, duration: duration || 1.6, ease: 'power2.out',
          onUpdate: function () {
            var s = obj.v.toFixed(decimals);
            if (comma) s = s.replace('.', ',');
            if (s === last) return; // touching textContent forces a reflow — only when the digits change
            last = s;
            node.textContent = s + suffix;
          }
        });
      }
    });
  }

  /* ---------- global bits (all viewports) ---------- */

  function initGlobal() {

    // 1. HERO INTRO — plays immediately on load (no ScrollTrigger = no risk)
    // DESKTOP ONLY: mobile Safari starves rAF during page load and froze
    // the timeline mid-flight on a real iPhone (title lines left shifted
    // and skewed, CTAs stuck hidden). Mobile renders the hero instantly.
    var hero = document.querySelector('.nm-hero-slider');
    var heroIntroOK = window.matchMedia && window.matchMedia('(min-width: 769px)').matches;
    if (hero && heroIntroOK) {
      var lines = gsap.utils.toArray('.nm-hs-content.is-visible .nm-hero-title-line', hero);
      var desc = hero.querySelector('.nm-hs-content.is-visible .nm-hero-desc');
      var ctas = gsap.utils.toArray('.nm-hs-content.is-visible .nm-hero-ctas .nm-btn', hero);
      var stats = gsap.utils.toArray('.nm-hs-content.is-visible .nm-hero-stats > div', hero);
      var tabs = hero.querySelector('.nm-hs-tabs');
      var visual = hero.querySelector('.nm-hero-visual');
      if (tabs) prep([tabs]);

      var intro = gsap.timeline({ delay: 0.15 });
      /* CRITICAL elements (title, can) never animate opacity: a starved
         timeline (mobile Safari at load) must NEVER leave them invisible —
         an interrupted transform just looks slightly shifted, that's fine */
      if (lines.length) intro.from(lines, { y: 90, skewY: 4, duration: 1.0, stagger: 0.14, ease: 'power4.out' }, 0);
      if (visual) intro.from(visual, { scale: 0.82, rotation: -5, duration: 1.3, ease: 'elastic.out(1, 0.55)' }, 0.15);
      if (tabs) intro.from(tabs, { y: -24, autoAlpha: 0, duration: 0.7, ease: 'power3.out' }, 0.1);
      if (desc) intro.from(desc, { y: 30, autoAlpha: 0, duration: 0.8, ease: 'power3.out' }, 0.45);
      if (ctas.length) intro.from(ctas, { y: 26, autoAlpha: 0, scale: 0.9, duration: 0.7, stagger: 0.1, ease: 'back.out(1.6)' }, 0.6);
      if (stats.length) intro.from(stats, { y: 20, autoAlpha: 0, duration: 0.6, stagger: 0.08, ease: 'power2.out' }, 0.8);
      /* HARD FAILSAFE — a `.from(autoAlpha:0)` timeline that gets starved
         (mobile Safari busy at load, backgrounded tab, throttled rAF) leaves
         the hero visual stuck at visibility:hidden forever (seen on
         iPhone: no can at all). If the intro hasn't finished after 3s,
         jump it to its final state. */
      setTimeout(function () {
        if (intro.progress() < 1) intro.progress(1);
      }, 3000);
      document.addEventListener('visibilitychange', function () {
        if (!document.hidden && intro.progress() < 1) intro.progress(1);
      });
    }

    // 2. Counters — hero stats + ingredient dosages (story stats have their own)
    gsap.utils.toArray('.nm-hero-stat-value, .nm-ingredient-dosage').forEach(function (el) { countUp(el); });

    // --- PHASE A2: below-the-fold hides can wait one macrotask ---
    setTimeout(function () {
    // 3. Trust strip — pop cascade
    entrance('.nm-trust-strip-item', { y: 34, scale: 0.75 }, { ease: 'back.out(1.8)', duration: 0.7 }, 0.07);

    // 4. Comparison — rows sweep in from the left, header pops
    entrance('.nm-comparison-row:not(.nm-comparison-row--head)', { x: -70 }, { duration: 0.8 }, 0.06);
    entrance('.nm-comparison-col-header', { y: -34, scale: 0.9 }, { ease: 'back.out(1.7)' }, 0.12);

    // 5. Ingredients — 3D flip-in cascade
    // (rotationX kept SMALL: ~10 perspective-rasterized cards animating at
    //  once tanked scroll FPS to ~21; will-change promotes them to layers
    //  for the entrance, then is dropped so we don't hold GPU memory)
    (function () {
      var cards = gsap.utils.toArray('.nm-ingredient-card');
      if (!cards.length) return;
      cards.forEach(function (el) {
        el.style.willChange = 'transform, opacity';
        hide(el, { rotationX: 16, y: 48, transformPerspective: 900, transformOrigin: '50% 0%' });
      });
      ST.batch(cards, {
        start: 'top 90%', once: true,
        onEnter: function (batch) {
          batch.forEach(function (el, i) {
            reveal(el, { duration: 0.8, ease: 'power3.out', delay: i * 0.06, onComplete: function () {
              el.style.willChange = 'auto';
            } });
          });
        }
      });
    })();

    }, 16); // end PHASE A2
    // --- PHASE B: below-the-fold entrances yield to the event loop first
    // (one 133ms boot task became two <70ms tasks — TBT on mobile) ---
    setTimeout(function () {
    // 6. Testimonials — tilt-in from alternating sides
    (function () {
      var cards = gsap.utils.toArray('.nm-proof-card');
      cards.forEach(function (el, i) { hide(el, { y: 76, rotation: i % 2 ? 4 : -4 }); });
      if (cards.length) ST.batch(cards, {
        start: 'top 90%', once: true,
        onEnter: function (batch) {
          batch.forEach(function (el, i) { reveal(el, { duration: 0.95, ease: 'power3.out', delay: i * 0.12 }); });
        }
      });
    })();

    // 7. Instagram — grid zoom cascade from center
    (function () {
      var grid = document.querySelector('.nm-insta-grid');
      if (!grid) return;
      var tiles = gsap.utils.toArray(grid.children);
      tiles.forEach(function (el) { hide(el, { scale: 0.78, y: 30 }); });
      ST.batch(tiles, {
        start: 'top 92%', once: true,
        onEnter: function (batch) {
          batch.forEach(function (el, i) { reveal(el, { duration: 0.9, ease: 'power3.out', delay: i * 0.09 }); });
        }
      });
    })();

    // 8. FAQ — cascade
    entrance('.nm-faq-item', { x: -46 }, { duration: 0.7 }, 0.06);

    }, 0);
    setTimeout(function () { // PHASE B2
    // 9. Quiz — options pop
    entrance('.nm-quiz-option', { y: 40, scale: 0.92 }, { ease: 'back.out(1.5)' }, 0.1);

    // 10. Beam dividers — the light beam charges across
    gsap.utils.toArray('.nm-beam-line, .nm-beam-track').forEach(function (el) {
      prep([el]);
      gsap.fromTo(el, { scaleX: 0.15, transformOrigin: '50% 50%', opacity: 0.4 }, {
        scaleX: 1, opacity: 1, ease: 'power2.out',
        scrollTrigger: { trigger: el, start: 'top 95%', end: 'top 60%', scrub: 0.6 }
      });
    });

    // 10b. Collection grid — cards pop in with a stagger
    entrance('.nm-collection-grid .nm-product-card', { y: 46, scale: 0.93 }, { ease: 'back.out(1.5)', duration: 0.8 }, 0.08);

    // 12. Content pages — about / retailers / partner / recipes (blog reuses
    // the collection-grid pattern already handled by block 10b).
    // KPI cards: their own .nm-abt-count/data-target counter (inline IIFE in
    // page-about.liquid) is untouched — this only staggers the card shells.
    entrance('.nm-abt-kpis .nm-abt-kpi', { y: 30, scale: 0.92 }, { ease: 'back.out(1.6)', duration: 0.65 }, 0.08);
    entrance('.nm-abt-products-grid .nm-abt-product-card', { y: 46, scale: 0.93 }, { ease: 'back.out(1.5)', duration: 0.8 }, 0.1);
    (function () {
      // timeline: alternates side like the comparison rows (odd/even already
      // mirrors flex-direction in CSS, so entrance direction follows suit).
      var items = gsap.utils.toArray('.nm-abt-tl-item');
      if (!items.length) return;
      items.forEach(function (el, i) { hide(el, { x: i % 2 ? 70 : -70 }); });
      ST.batch(items, {
        start: 'top 90%', once: true,
        onEnter: function (batch) {
          batch.forEach(function (el, i) { reveal(el, { duration: 0.8, ease: 'power3.out', delay: i * 0.08 }); });
        }
      });
    })();
    (function () {
      // values grid: 3D flip-in cascade, same recipe as ingredient cards.
      var cards = gsap.utils.toArray('.nm-abt-val-grid .nm-abt-val-card');
      if (!cards.length) return;
      cards.forEach(function (el) { hide(el, { rotationX: 16, y: 44, transformPerspective: 900, transformOrigin: '50% 0%' }); });
      ST.batch(cards, {
        start: 'top 90%', once: true,
        onEnter: function (batch) {
          batch.forEach(function (el, i) { reveal(el, { duration: 0.9, ease: 'power3.out', delay: i * 0.08 }); });
        }
      });
    })();
    entrance('.nm-ret-channels .nm-ret-channel', { y: 40, scale: 0.9 }, { ease: 'back.out(1.6)', duration: 0.75 }, 0.1);
    entrance('.nm-ret-grid .nm-ret-card', { y: 36, scale: 0.95 }, { duration: 0.7 }, 0.05);
    entrance('.nm-partner-types-grid .nm-partner-type-card', { y: 46, scale: 0.93 }, { ease: 'back.out(1.5)', duration: 0.8 }, 0.1);
    (function () {
      // benefits: alternating side sweep, same idea as comparison rows.
      var items = gsap.utils.toArray('.nm-partner-benefits-grid .nm-partner-benefit');
      if (!items.length) return;
      items.forEach(function (el, i) { hide(el, { x: i % 2 ? 60 : -60 }); });
      ST.batch(items, {
        start: 'top 90%', once: true,
        onEnter: function (batch) {
          batch.forEach(function (el, i) { reveal(el, { duration: 0.75, ease: 'power3.out', delay: i * 0.07 }); });
        }
      });
    })();
    (function () {
      // recipe cards carry an image: 3D flip-in like ingredients.
      var cards = gsap.utils.toArray('.nm-recipes-grid .nm-recipe-card');
      if (!cards.length) return;
      cards.forEach(function (el) { hide(el, { rotationX: 16, y: 46, transformPerspective: 900, transformOrigin: '50% 0%' }); });
      ST.batch(cards, {
        start: 'top 90%', once: true,
        onEnter: function (batch) {
          batch.forEach(function (el, i) { reveal(el, { duration: 0.9, ease: 'power3.out', delay: i * 0.07 }); });
        }
      });
    })();

    // 11. Product page entrance — load-time tweens only (no ScrollTrigger = no risk)
    if (document.body.className.indexOf('template-product') !== -1) {
      var pgal = document.querySelector('.nm-product-gallery');
      var pinfo = gsap.utils.toArray('.nm-product-info > *').slice(0, 8);
      if (pgal) {
        prep([pgal]);
        gsap.from(pgal, { x: -44, autoAlpha: 0, duration: 0.9, ease: 'power3.out', delay: 0.15 });
      }
      if (pinfo.length) {
        prep(pinfo);
        gsap.from(pinfo, { y: 26, autoAlpha: 0, duration: 0.7, stagger: 0.07, ease: 'power2.out', delay: 0.3 });
      }
    }
    }, 32);
    setTimeout(function () { // PHASE B3
    // D. BRAND STORY (all viewports — the frame-driven ticker is cheap,
    // and mobile deserves the manifesto highlight too) — pinned scene: the manifesto lights up word by word
    (function () {
      var story = document.querySelector('.nm-story');
      var quote = document.querySelector('.nm-story-quote');
      if (!story || !quote) return;

      // wrap every word in a span, preserving nested accent spans
      var words = [];
      (function split(node) {
        Array.prototype.slice.call(node.childNodes).forEach(function (child) {
          if (child.nodeType === 3) {
            var parts = child.textContent.split(/(\s+)/);
            var frag = document.createDocumentFragment();
            parts.forEach(function (p) {
              if (!p) return;
              if (/^\s+$/.test(p)) { frag.appendChild(document.createTextNode(p)); return; }
              var s = document.createElement('span');
              s.className = 'nm-w';
              s.textContent = p;
              frag.appendChild(s);
              words.push(s);
            });
            node.replaceChild(frag, child);
          } else if (child.nodeType === 1 && !child.classList.contains('nm-w')) {
            split(child);
          }
        });
      })(quote);
      if (!words.length) return;

      var dim = document.documentElement.getAttribute('data-theme') === 'light' ? 0.2 : 0.14;
      gsap.set(words, { display: 'inline-block' });
      var img = document.querySelector('.nm-story-img-wrap') || document.querySelector('.nm-story-img');

      try {
        /* SCROLL-LINKED & REVERSIBLE (explicit client requirement): words
           light up scrolling down, dim back scrolling up.
           NOT ScrollTrigger-driven: cached trigger positions went stale on
           this section four times (lazy images + pins above kept shifting
           the layout; measured start=-2292 while the quote sat at ~9500px).
           Instead the timeline progress is computed EVERY FRAME from the
           quote's real on-screen position — immune to stale caches,
           reversible by construction. One getBoundingClientRect per frame
           on one element is negligible. */
        var stl = gsap.timeline({ paused: true });
        stl.fromTo(words,
          { opacity: dim, y: 12 },
          { opacity: 1, y: 0, stagger: 0.25, duration: 0.5, ease: 'none' }, 0);
        var cur = 0;
        /* IO gate: no layout read AT ALL while the quote is far off-screen
           (the per-frame getBoundingClientRect was itself a mobile CPU tax) */
        var near = false;
        if ('IntersectionObserver' in window) {
          new IntersectionObserver(function (en) { near = en[0].isIntersecting; },
            { rootMargin: '250px 0px 250px 0px' }).observe(quote);
        } else { near = true; }
        gsap.ticker.add(function () {
          if (!near) return;
          var r = quote.getBoundingClientRect();
          if (r.bottom < -200 || r.top > window.innerHeight + 200) return; // idle off-screen
          var vh = window.innerHeight;
          var t = gsap.utils.clamp(0, 1, (vh * 0.85 - r.top) / (vh * 0.55));
          cur += (t - cur) * 0.14;                    // scrub-like smoothing
          if (Math.abs(t - cur) < 0.001) cur = t;
          stl.progress(cur);
        });
        if (img) {
          prep([img]);
          gsap.fromTo(img, { scale: 1.1, y: 40 }, {
            scale: 1, y: -20, ease: 'none',
            scrollTrigger: { trigger: img, start: 'top 90%', end: 'top 15%', scrub: 0.6 }
          });
        }
      } catch (e) {
        gsap.set(words, { opacity: 1 }); // never leave the manifesto dim
      }
    })();


    }, 64); // end PHASE B3
  }

  /* ---------- desktop-only show ---------- */

  function initDesktop() {

    // A. HERO scroll-out — content and can drift apart with depth
    var hero = document.querySelector('.nm-hero-slider');
    if (hero) {
      var content = hero.querySelector('.nm-hero-content');
      var visual = hero.querySelector('.nm-hero-visual');
      var tl = gsap.timeline({
        scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: 0.8 }
      });
      if (content) tl.to(content, { y: -110, autoAlpha: 0.25, ease: 'none' }, 0);
      if (visual) tl.to(visual, { y: 140, rotation: 6, scale: 0.9, ease: 'none' }, 0);
    }

    // B. PRODUCT SPLIT — panels converge like a versus screen
    (function () {
      var panels = gsap.utils.toArray('.nm-split-panel');
      if (panels.length !== 2) return;
      var wrap = panels[0].closest('.nm-split') || panels[0].parentElement;
      prep(panels);
      gsap.fromTo(panels[0], { xPercent: -13, autoAlpha: 0.4 }, {
        xPercent: 0, autoAlpha: 1, ease: 'power2.out',
        scrollTrigger: { trigger: wrap, start: 'top 88%', end: 'top 30%', scrub: 0.7 }
      });
      gsap.fromTo(panels[1], { xPercent: 13, autoAlpha: 0.4 }, {
        xPercent: 0, autoAlpha: 1, ease: 'power2.out',
        scrollTrigger: { trigger: wrap, start: 'top 88%', end: 'top 30%', scrub: 0.7 }
      });
      // cans rise out of the panels
      gsap.utils.toArray('.nm-split-can').forEach(function (can, i) {
        prep([can]);
        gsap.fromTo(can, { y: 110, rotation: i ? 8 : -8 }, {
          y: 0, rotation: 0, ease: 'power2.out',
          scrollTrigger: { trigger: wrap, start: 'top 80%', end: 'top 25%', scrub: 0.7 }
        });
      });
    })();

    // C. MARQUEE — skews with scroll velocity like it's alive
    (function () {
      var mq = document.querySelector('.nm-marquee');
      if (!mq) return;
      var toSkew = gsap.quickTo(mq, 'skewX', { duration: 0.4, ease: 'power2.out' });
      ST.create({
        trigger: mq, start: 'top bottom', end: 'bottom top',
        onUpdate: function (self) {
          var v = gsap.utils.clamp(-14, 14, self.getVelocity() / -220);
          toSkew(v);
        },
        onLeave: function () { toSkew(0); },
        onLeaveBack: function () { toSkew(0); }
      });
    })();

    // E. CTA finale — heading zooms in as you arrive
    (function () {
      var h = document.querySelector('.nm-cta-content .nm-h2');
      var cta = document.querySelector('.nm-cta-content');
      if (!h || !cta) return;
      prep([h]);
      gsap.fromTo(h, { scale: 0.8, autoAlpha: 0.3 }, {
        scale: 1, autoAlpha: 1, ease: 'power2.out',
        scrollTrigger: { trigger: cta, start: 'top 90%', end: 'top 45%', scrub: 0.7 }
      });
    })();

    // E2. Magnetic buttons — subtle pull toward the cursor
    gsap.utils.toArray('.nm-btn').forEach(function (btn) {
      var strength = 0.35;
      btn.addEventListener('mousemove', function (e) {
        var r = btn.getBoundingClientRect();
        gsap.to(btn, { x: (e.clientX - r.left - r.width / 2) * strength, y: (e.clientY - r.top - r.height / 2) * strength, duration: 0.4, ease: 'power3.out' });
      });
      btn.addEventListener('mouseleave', function () {
        gsap.to(btn, { x: 0, y: 0, duration: 0.5, ease: 'elastic.out(1, 0.4)' });
      });
    });

    // F. 3D hover tilt on cards
    gsap.utils.toArray('.nm-ingredient-card, .nm-proof-card, .nm-quiz-option, .nm-abt-product-card, .nm-abt-val-card, .nm-partner-type-card, .nm-recipe-card, .nm-ret-channel, .nm-ret-card').forEach(function (card) {
      var rx = gsap.quickTo(card, 'rotationX', { duration: 0.45, ease: 'power2.out' });
      var ry = gsap.quickTo(card, 'rotationY', { duration: 0.45, ease: 'power2.out' });
      gsap.set(card, { transformPerspective: 900 });
      card.addEventListener('mousemove', function (e) {
        var r = card.getBoundingClientRect();
        ry(((e.clientX - r.left) / r.width - 0.5) * 10);
        rx(((e.clientY - r.top) / r.height - 0.5) * -10);
      });
      card.addEventListener('mouseleave', function () { rx(0); ry(0); });
    });

    // F2. Liquid distortion on photos (SVG turbulence, hover-only so it's cheap)
    (function () {
      var imgs = gsap.utils.toArray('.nm-story-img-wrap img, .nm-insta-grid img, .nm-live-strip-grid img');
      if (!imgs.length) return;
      var NS = 'http://www.w3.org/2000/svg';
      var svg = document.createElementNS(NS, 'svg');
      svg.setAttribute('width', '0'); svg.setAttribute('height', '0');
      svg.style.position = 'absolute';
      svg.innerHTML =
        '<filter id="nm-liquid"><feTurbulence type="fractalNoise" baseFrequency="0.012 0.028" numOctaves="2" result="n" seed="3"/>' +
        '<feDisplacementMap in="SourceGraphic" in2="n" scale="0" xChannelSelector="R" yChannelSelector="G"/></filter>';
      document.body.appendChild(svg);
      var disp = svg.querySelector('feDisplacementMap');
      var turb = svg.querySelector('feTurbulence');
      var state = { scale: 0, freq: 0.012 };
      var active = null;
      function render() {
        disp.setAttribute('scale', state.scale);
        turb.setAttribute('baseFrequency', state.freq + ' ' + state.freq * 2.2);
      }
      imgs.forEach(function (img) {
        img.addEventListener('mouseenter', function () {
          if (active && active !== img) active.style.filter = '';
          active = img;
          img.style.filter = 'url(#nm-liquid)';
          gsap.killTweensOf(state);
          gsap.to(state, { scale: 34, freq: 0.02, duration: 0.5, ease: 'power2.out', onUpdate: render });
          gsap.to(state, { scale: 0, freq: 0.012, duration: 0.9, ease: 'power2.inOut', delay: 0.5, onUpdate: render,
            onComplete: function () { img.style.filter = ''; if (active === img) active = null; } });
        });
        img.addEventListener('mouseleave', function () {
          gsap.killTweensOf(state);
          gsap.to(state, { scale: 0, duration: 0.4, ease: 'power2.out', onUpdate: render,
            onComplete: function () { img.style.filter = ''; if (active === img) active = null; } });
        });
      });
    })();

    // G. Ambient depth — ingredient grid columns drift at different speeds
    (function () {
      var grid = document.querySelector('.nm-ingredients-grid');
      if (!grid) return;
      var cards = gsap.utils.toArray(grid.children);
      cards.forEach(function (card, i) {
        var speed = (i % 3) - 1; // -1, 0, 1 per column
        if (!speed) return;
        // yPercent stays independent from the entrance tween's `y`
        gsap.to(card, {
          yPercent: speed * -7, ease: 'none',
          scrollTrigger: { trigger: grid, start: 'top bottom', end: 'bottom top', scrub: 1.2 }
        });
      });
    })();
  }

  /* ---------- boot ---------- */

  function boot() {
    var desktop = window.matchMedia('(min-width: 769px)').matches;
    if (desktop) {
      initGlobal(); // hero intro must start with the first frame
    } else {
      // mobile: nothing here is load-critical (hero renders statically) —
      // move the whole init off the load window to cut TBT long tasks
      var idle = window.requestIdleCallback || function (f) { setTimeout(f, 350); };
      idle(function () { initGlobal(); ST.refresh(); }, { timeout: 1500 });
    }
    // scroll choreography isn't needed in the first second — deferring it
    // splits the boot long-task in two (TBT) without visible difference
    var idleD = window.requestIdleCallback || function (f) { setTimeout(f, 300); };
    idleD(function () {
      ST.matchMedia({
        '(min-width: 769px)': initDesktop
      });
      ST.refresh();
    }, { timeout: 1200 });
    // The theme has a loader overlay + lazy images: refresh measurements late.
    window.addEventListener('load', function () {
      ST.refresh();
      setTimeout(function () { ST.refresh(); }, 1200);
    });
    // Lazy images load DURING scroll and shift the layout below them,
    // making every precomputed trigger stale (scrubs appear "already done").
    // Refresh (debounced) each time one arrives.
    var rT;
    function lazyRefresh() {
      // NOTE: never defer while pinned — deferring left the anatomy pin on
      // stale positions after a mid-page reload (scene overlapped the split
      // section). ScrollTrigger reverts pins during refresh natively.
      clearTimeout(rT);
      rT = setTimeout(function () { ST.refresh(); }, 400);
    }
    document.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', lazyRefresh, { once: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
